import React, {useContext, useEffect, useRef, useState} from 'react';
import {Button, Icon, IconButton, Skeleton, useDisclosure} from '@chakra-ui/react';
import PersonaInSharedExperienceListItem from './personaInSharedExperienceListItem';
import {Box, Flex, Text, Stack} from '@chakra-ui/layout';
import {MdAdd, MdArrowBack, MdFace} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { SharedExperiencesContext } from '../../lib/sharedExperiencesProvider';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay
} from '@chakra-ui/modal';
import AddPersonaToSharedExperienceForm from './addPersonaToSharedExperienceForm';
import PersonaForm from '@/persona/components/personaForm';
import PersonaProvider from '@/persona/lib/personaProvider';
import { SharedExperiencePageContext } from '../lib/sharedExperiencePageContext';
import usePersonaApi from '@/persona/lib/usePersonaApi';
import { useSharedExperiencesApi } from '../../lib/useSharedExperiencesApi';
import useTranslation from '../../../lib/i18n/useTranslation';

export default function PersonaInSharedExperiencesList(props) {
  const { t } = useTranslation();
  const sharedExperiencesApi = useSharedExperiencesApi();
  const {
    rights,
  } = useContext(SharedExperiencesContext);
  const {
    refreshPersonaList,
    refreshExperienceList,
  } = useContext(SharedExperiencePageContext);
  const personaApi = usePersonaApi();
  const {sharedExperienceId} = props;
  const [isAdding, setIsAdding] = useState(false);
  const [linkedPersonas, setLinkedPersonas] = useState([]);
  const [isFetchingPersonas, setIsFetchingPersonas] = useState(false);
  const navigate = useNavigate();
  const modalAddPersona = useDisclosure();
  const modalCreatePersona = useDisclosure();
  const ref1 = useRef(null);
  const ref2 = useRef(null);

  const loadLinkedPersonas = async () => {
    try {
      setIsFetchingPersonas(true);
      const personasData = await personaApi.fetchManyPersonasBySharedExperienceId(sharedExperienceId);
      setLinkedPersonas(personasData || []);
      setIsFetchingPersonas(false);
    } catch (error) {
      console.error('Error loading linked personas:', error);
      setIsFetchingPersonas(false);
    }
  };

  useEffect(() => {
    if (sharedExperienceId) {
      loadLinkedPersonas();
    }
  }, [sharedExperienceId]);

  const linkNewPersonaToSharedExperience = async (newPersona) => {
    try {
      await sharedExperiencesApi.linkPersonaToSharedExperience(sharedExperienceId, newPersona.id)
      await loadLinkedPersonas();
      setIsAdding(false);
      modalCreatePersona.onClose();
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <>
      <Box w="100%">
        <Flex 
        alignItems='center'
        mb={2}
        justifyContent={"space-between"}
        px={3}
        >
          <Flex
            alignItems='center'
          >
            <Icon
              as={MdFace}
              color={'text.primary'}
              boxSize={'14px'}
              mr={2}
            />
            <Text fontSize={14} >{t('sharedExperience.personas')}</Text>
          </Flex>
          {rights.can_update && (
            <IconButton
              ml={2}
              variant="greenOutline"
              icon={<MdAdd />}
              size="sm"
              onClick={modalAddPersona.onOpen}
            />
          )}
        </Flex>
        <Box maxH={300} overflowY={'auto'}>
          { isFetchingPersonas && (
            <Stack p={3} spacing={2} backgroundColor={'white'}>
               <Skeleton height='30px' />
               <Skeleton height='30px' />
               <Skeleton height='30px' />
             </Stack>
          )}
          { (!isFetchingPersonas && linkedPersonas && linkedPersonas.length === 0) && (
              <Box px={3}>
                <Text color="text.light.grey">{t('sharedExperience.noPersonas')}</Text>
              </Box>
          )}
           { (!isFetchingPersonas && linkedPersonas) && linkedPersonas.map((p, i) => {
            return (
              <PersonaProvider
                personaId={p.id}
                originalPersona={p}
                needEditionRequest={false}
                key={i}>
                  <Flex
                    style={{'cursor' : 'pointer'}}
                    onClick={() => {
                      navigate( `/persona/${p.id}`)
                    }}
                  >
                    <PersonaInSharedExperienceListItem
                      sharedExperienceId={sharedExperienceId}
                      onUpdate={() => {
                        loadLinkedPersonas();
                        refreshExperienceList();
                      }}
                    />
                </Flex>
              </PersonaProvider>
            );
          })}
        </Box>
      </Box>
      <Modal isOpen={modalAddPersona.isOpen} onClose={modalAddPersona.onClose}>
        <ModalOverlay  />
        <ModalContent >
          <ModalHeader>{t('sharedExperience.addPersonaToSharedExperience')}</ModalHeader>
          <ModalBody >
            <Box p={3}>
              <AddPersonaToSharedExperienceForm
                sharedExperienceId={sharedExperienceId}
                hideSave={true}
                onCreateNew={() => {
                  modalAddPersona.onClose();
                  modalCreatePersona.onOpen();
                }}
                onSubmitting={() => {
                  setIsAdding(true);
                }}
                onSubmitted={() => {
                  setIsAdding(false);
                  modalAddPersona.onClose();
                  loadLinkedPersonas();
                }}
                ref={ref1}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='greenOutline'
              onClick={modalAddPersona.onClose}
            >{t('common.close')}</Button>
            <Button
              variant="greenFill"
              ml={2}
              isLoading={isAdding}
              onClick={() => {
                ref1.current.click();
              }}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={modalCreatePersona.isOpen} onClose={modalCreatePersona.onClose}>
        <ModalOverlay  />
        <ModalContent >
          <ModalHeader>
            <IconButton
              fontSize='24'
              variant="tertiary"
              icon={<MdArrowBack/>}
              aria-label='New'
              mr={2}
              onClick={() => {
                modalCreatePersona.onClose();
                modalAddPersona.onOpen();
              }}
            />
            New Persona</ModalHeader>
          <ModalBody >
            <Box p={3}>
              <PersonaForm
                hideSave={true}
                onSubmitting={() => {
                  setIsAdding(true);
                }}
                onSubmitted={(newPersona) => {
                  linkNewPersonaToSharedExperience(newPersona)
                }}
                ref={ref2}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='greenOutline'
              onClick={() => {
                modalCreatePersona.onClose();
                modalAddPersona.onOpen();
              }}
            >{t('common.close')}</Button>
            <Button
              variant="greenFill"
              ml={2}
              isLoading={isAdding}
              onClick={() => {
                ref2.current.click();
              }}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
} 